import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useLocalizedPricingPlans, type PricingPlan } from '../utils/localizedPlans';

interface PlanSelectionStepProps {
  plans: PricingPlan[];
  onBack: () => void;
  onChoose: (plan: PricingPlan) => void;
  embedded?: boolean;
  hideBack?: boolean;
}

export const PlanSelectionStep: React.FC<PlanSelectionStepProps> = ({ plans, onBack, onChoose, embedded, hideBack }) => {
  const { t } = useTranslation();
  const localizedPlans = useLocalizedPricingPlans(plans);

  return (
    <div className={embedded ? 'p-0' : 'max-w-5xl mx-auto p-4 sm:p-6'}>
      <div className="flex items-center justify-between mb-4 sm:mb-6 gap-3 flex-wrap">
        {!hideBack ? (
          <button
            type="button"
            className="text-sm sm:text-base text-blue-600 flex items-center gap-1"
            onClick={onBack}
            aria-label={t('auth_plan_back_templates')}
          >
            <span className="inline-block sm:hidden">{t('auth_plan_back_short')}</span>
            <span className="hidden sm:inline">{t('auth_plan_back_templates')}</span>
          </button>
        ) : (
          <div />
        )}
        {!embedded && <LanguageSwitcher compact />}
        {!embedded && <div className="text-xs text-gray-500 sm:text-sm">{t('auth_plan_step')}</div>}
      </div>
      <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">{t('auth_plan_title')}</h2>
      <p className="text-gray-600 text-sm sm:text-base mb-5 sm:mb-6 leading-relaxed">{t('auth_plan_subtitle')}</p>
      <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {localizedPlans.map((p, idx) => {
          const features: string[] = Array.isArray(p.features) ? p.features : [];
          const collapsed = features.length > 6;
          return (
            <div
              key={p.id || idx}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-5 flex flex-col focus-within:shadow-md hover:shadow-md transition-shadow h-full"
            >
              <div className="flex items-center justify-between mb-2 gap-2">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate" title={p.name}>
                  {p.name}
                </h3>
                {p.isPopular && (
                  <span className="text-[10px] sm:text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800 shrink-0">
                    {t('auth_plan_popular')}
                  </span>
                )}
              </div>
              <div className="text-xl sm:text-2xl font-bold text-blue-700 mb-1 sm:mb-2">{p.price}</div>
              <p className="text-xs sm:text-sm text-gray-600 mb-3 line-clamp-3 sm:line-clamp-none">{p.description}</p>
              <ul className="text-xs sm:text-sm text-gray-700 space-y-1 mb-3 sm:mb-4 max-h-32 overflow-y-auto sm:max-h-none sm:overflow-visible pr-1 flex-1">
                {features.slice(0, collapsed ? 6 : features.length).map((f: string, i: number) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mr-1 flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
                {collapsed && (
                  <li className="text-[11px] sm:text-xs text-blue-600">
                    {t('auth_plan_more', { count: features.length - 6 })}
                  </li>
                )}
              </ul>
              <button
                type="button"
                className={`mt-auto w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl text-white text-sm sm:text-base font-medium ${p.buttonColor || 'bg-blue-600'} hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                onClick={() => onChoose(p)}
                aria-label={`${t('auth_plan_choose')} ${p.name}`}
              >
                {p.buttonText || t('auth_plan_choose')}
              </button>
              {Number(String(p.price || '').replace(/[^0-9.]/g, '')) === 0 && (
                <div className="mt-2 text-[11px] sm:text-xs text-green-600">{t('auth_plan_instant_free')}</div>
              )}
            </div>
          );
        })}
        {localizedPlans.length === 0 && (
          <div className="col-span-full bg-white border border-dashed border-gray-300 rounded-2xl p-6 text-center text-gray-500 text-sm">
            {t('auth_plan_none')}
          </div>
        )}
      </div>
    </div>
  );
};
